"use client";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { LayoutGrid } from "lucide-react";
import MediaComponent from "@core/helpers/server/components/image";
import SwiperComponent from "@/components/ui/swiper-component";
import Title from "@/components/Title";
import DeviceOnly from "@/components/DeviceOnly";

const ALL_CATEGORIES_KEY = "__all__";

const normalize_path = (value) => {
    if (!value) return "";

    const path = String(value).split("?")[0].split("#")[0];

    return path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
};

function CategoryCard({ item, storage_prefix_url, compact, isActive }) {

    const isAllItem = item?.key === ALL_CATEGORIES_KEY;

    return (
        <Link
            href={item?.seo?.full_url || "#"}
            className={`group flex flex-col items-center justify-center w-full pt-2 ${compact ? "" : "space-y-4"}`}
            style={{ minWidth: 0 }}>
            {compact ?

                <div className={`flex flex-col items-center justify-center gap-2 w-full h-[146px] p-2 rounded-md border transition-colors
                    ${isActive
                        ? "bg-jmt-neutral-100 border-jmt-neutral-200"
                        : "border-transparent hover:bg-jmt-neutral-100"}`}>
                    <div className="w-[72px] h-[72px] lg:w-[100px] lg:h-[100px] flex items-center justify-center">
                        {isAllItem ? (
                            <LayoutGrid
                                className="w-9 h-9 lg:w-12 lg:h-12 text-jmt-neutral-500"
                                strokeWidth={1.5}
                            />
                        ) : (
                            <MediaComponent
                                src={item?.media?.url || ""}
                                alt={item?.media?.alt || item?.media?.title}
                                width={100}
                                height={100}
                                storage_prefix_url={storage_prefix_url}
                                // آیکن دسته ۷۲px موبایل / ۱۰۰px دسکتاپ
                                sizes="100px"
                                style={{ width: "100%", height: "100%" }}
                                image_className="w-full h-full object-cover"
                            />
                        )}
                    </div>
                    <span className="text-xs font-bold text-jmt-neutral-700 text-center whitespace-nowrap">
                        {item?.title}
                    </span>
                </div>
                :
                <>
                    <div className="w-full flex justify-center">
                        <div className="relative flex items-center justify-center w-[57px] h-[57px] lg:w-20 lg:h-20 bg-jmt-neutral-100 border border-jmt-neutral-200 rounded-md">
                            <div className="absolute w-[72px] h-[72px] lg:w-[100px] lg:h-[100px]">
                                <MediaComponent
                                    src={item?.media?.url || ""}
                                    alt={item?.media?.alt || item?.media?.title}
                                    width={100}
                                    height={100}
                                    storage_prefix_url={storage_prefix_url}
                                    // آیکن دسته ۷۲px موبایل / ۱۰۰px دسکتاپ
                                    sizes="100px"
                                    style={{ width: "100%", height: "100%" }}
                                    image_className="w-full h-full object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Title */}
                    <span className="text-xs font-bold text-jmt-neutral-700 text-center whitespace-nowrap">
                        {item?.title}
                    </span>
                </>}
        </Link>
    );
}

export default function HomeCategoryCollection({ storage_prefix_url, fields, collection_data, isHeaderSection }) {

    const pathname = usePathname();
    const isAmazingPage = pathname === "/landing/amazing-offers/";


    const compact = isHeaderSection || isAmazingPage;

    const source_categories = collection_data?.categories || [];

    const first_parent_url = source_categories?.[0]?.parents?.[0]?.seo?.full_url;

    const all_categories_url =
        first_parent_url &&
            source_categories.every(
                (cat) => cat?.parents?.[0]?.seo?.full_url === first_parent_url
            )
            ? first_parent_url
            : "/products/";

    const categories = compact
        ? [
            {
                id: ALL_CATEGORIES_KEY,
                key: ALL_CATEGORIES_KEY,
                title: "همه",
                seo: { full_url: all_categories_url },
            },
            ...source_categories,
        ]
        : source_categories;

    const current_path = normalize_path(pathname);

    const is_category_active = (cat) =>
        compact && normalize_path(cat?.seo?.full_url) === current_path;

    return (
        <>
            <DeviceOnly on="mobile">
                <div className="block lg:hidden w-full mx-auto">
                    {fields?.title &&
                        <Title
                            type={fields?.title?.type}
                            className="text-jmt-main font-bold mb-4 px-4 md:px-10" >
                            {fields?.title?.title}
                        </Title>}

                    <SwiperComponent
                        slides={categories?.length > 0 ? categories?.map((cat) => (
                            <CategoryCard
                                key={cat.id}
                                item={cat}
                                compact={compact}
                                isActive={is_category_active(cat)}
                                storage_prefix_url={storage_prefix_url} />
                        )) : []}
                        slidesPerView={3.5}
                        spaceBetween={12}
                        navigation={false}
                        navPosition="sides"
                        swiper_className="px-4! md:px-10!"
                        loop={false}
                        breakpoints={{
                            400: { slidesPerView: 4.5, spaceBetween: 12 },
                            500: { slidesPerView: 5.5, spaceBetween: 12 },
                            600: { slidesPerView: 6.5, spaceBetween: 12 },
                            768: { slidesPerView: 7.5, spaceBetween: 20 },
                            868: { slidesPerView: 8.5, spaceBetween: 22 },
                            968: { slidesPerView: 9.5, spaceBetween: 24 },
                            1100: { slidesPerView: 10.5, spaceBetween: 24 },
                        }}
                    />
                </div>
            </DeviceOnly>

            <DeviceOnly on="desktop">
                <div className="hidden lg:block">
                    <div className="container">
                        {fields?.title &&
                            <Title
                                type={fields?.title?.type}
                                className="text-jmt-main font-bold mb-4" >
                                {fields?.title?.title}
                            </Title>}

                        <SwiperComponent
                            slides={categories?.length > 0 ? categories?.map((cat) => (
                                <CategoryCard
                                    key={cat.id}
                                    item={cat}
                                    compact={compact}
                                    isActive={is_category_active(cat)}
                                    storage_prefix_url={storage_prefix_url} />
                            )) : []}
                            slidesPerView={3.5}
                            spaceBetween={12}
                            navigation={true}
                            navPosition="sides"
                            loop={false}
                            breakpoints={{
                                400: { slidesPerView: 4.5, spaceBetween: 12 },
                                500: { slidesPerView: 5.5, spaceBetween: 12 },
                                600: { slidesPerView: 6.5, spaceBetween: 12 },
                                768: { slidesPerView: 7.5, spaceBetween: 20 },
                                868: { slidesPerView: 8.5, spaceBetween: 22 },
                                968: { slidesPerView: 9.5, spaceBetween: 24 },
                                1100: { slidesPerView: 10.5, spaceBetween: 24 },
                            }}
                        />
                    </div>
                </div>
            </DeviceOnly>
        </>
    );
}
